import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calculator, ArrowRight, Shield, FileText, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import CalculatorForm from "@/components/calculator/CalculatorForm";
import ResultsPanel from "@/components/calculator/ResultsPanel";
import EnhancedVerification from "@/components/EnhancedVerification";
import FeedbackBar from "@/components/FeedbackBar";
import SEOContent from "@/components/SEOContent";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { CalculationResults, CalculatorInputs } from "@/types/calculator";

const Index = () => {
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [inputs, setInputs] = useState<CalculatorInputs | null>(null);

  const handleCalculate = (calculatedResults: CalculationResults, calculatorInputs: CalculatorInputs) => {
    setResults(calculatedResults);
    setInputs(calculatorInputs);
  };

  const scrollToCalculator = () => {
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Walking Weight Loss Calculator — Estimate Calories & Weight Loss | Datalevo</title>
        <meta name="description" content="Walking Weight Loss Calculator — estimate calories burned, weekly totals, and realistic weight-change projections. Free, privacy-first, exportable plans." />
      </Helmet>

      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              Walking Weight Loss Calculator
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
              Estimate calories burned, weekly totals, and realistic weight-change projections. Free, privacy-first, and exportable.
            </p>
            <Button size="lg" onClick={scrollToCalculator} className="gap-2">
              Start Calculator <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Server-Rendered Featured Snippet */}
        <section className="py-6 bg-accent/10 border-y border-accent/20">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm md:text-base text-foreground font-medium">
              <strong>Quick Fact:</strong> A 30-minute brisk walk at 5 km/h burns approximately 124 kcal for a 70 kg adult; weekly totals depend on sessions per week.
            </p>
          </div>
        </section>

        {/* Calculator Section */}
        <section id="calculator" className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-6">
                <CalculatorForm onCalculate={handleCalculate} />
              </div>
              <div className="space-y-6">
                <ResultsPanel results={results} inputs={inputs} />
              </div>
            </div>
          </div>
        </section>

        {/* Verification Section */}
        <section className="py-8 md:py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <EnhancedVerification />
          </div>
        </section>

        {/* Page Summaries */}
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* About Summary */}
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">About Us</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Datalevo builds privacy-first, evidence-based tools. All calculations run locally in your browser.
                </p>
                <Link to="/about" className="text-sm text-primary hover:underline font-medium">
                  Learn more →
                </Link>
              </Card>

              {/* Contact Summary */}
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Contact</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Questions or feedback? Reach us at contact@datalevo.com. We respond within 24-48 hours.
                </p>
                <Link to="/contact" className="text-sm text-primary hover:underline font-medium">
                  Get in touch →
                </Link>
              </Card>

              {/* Legal Summary */}
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Legal</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Review our privacy policy, terms of use, and disclaimer for important information.
                </p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Link to="/privacy-policy" className="text-primary hover:underline">Privacy</Link>
                  <Link to="/terms-and-conditions" className="text-primary hover:underline">Terms</Link>
                  <Link to="/disclaimer" className="text-primary hover:underline">Disclaimer</Link>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Disclaimers */}
        <section className="py-6">
          <div className="container mx-auto px-4 space-y-4">
            <Card className="p-4 bg-secondary/30 border-card-border">
              <p className="text-xs md:text-sm text-muted-foreground text-center">
                🔒 <strong>Privacy First:</strong> All calculations run in your browser. No data is sent unless you share.
              </p>
            </Card>
            <Card className="p-4 bg-muted/50 border-card-border">
              <p className="text-xs text-muted-foreground text-center">
                ⚠️ <strong>Medical Disclaimer:</strong> Estimates are for information only. Consult a healthcare professional before starting any weight loss program.
              </p>
            </Card>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <SEOContent />
          </div>
        </section>
      </main>

      <FeedbackBar />
      <Footer />
    </div>
  );
};

export default Index;
